import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Paper, CircularProgress, IconButton, Tooltip, Chip,
  TextField, InputAdornment, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import BlockIcon from '@mui/icons-material/Block';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import {
  dashboardService,
  orderQualityCheckService,
  qualityCheckService,
  blockedStockService,
} from '../services/api';
import QCResultDrawer, { QC_RESULT_LABELS } from '../components/QCResultDrawer';
import QCKanbanPage from './QCKanbanPage';
import BlockingStockPage from './BlockingStockPage';
import { format, parseISO } from 'date-fns';
import { uk } from 'date-fns/locale';
import { fmtMachineLabel } from '../utils/machineLabel';

const MAIN_TABS = [
  { id: 'home', label: 'Головна', icon: <HomeOutlinedIcon fontSize="small" /> },
  { id: 'requests', label: 'Заявки', icon: <ViewKanbanOutlinedIcon fontSize="small" /> },
  { id: 'blocking', label: 'Блокування', icon: <BlockIcon fontSize="small" />, perm: 'menu.stock.blocking' },
  { id: 'history', label: 'Історія', icon: <HistoryIcon fontSize="small" /> },
];

const HOME_SUB = [
  { id: 'tpa', label: 'ТПА', icon: <PrecisionManufacturingIcon fontSize="small" /> },
  { id: 'production', label: 'Виробництво', icon: <FactoryOutlinedIcon fontSize="small" /> },
  { id: 'warehouse', label: 'Склад', icon: <WarehouseOutlinedIcon fontSize="small" /> },
];

function fmtDate(val) {
  if (!val) return '—';
  try { return format(parseISO(val), 'dd.MM.yyyy HH:mm', { locale: uk }); }
  catch { return val; }
}

function readStoredUser() {
  try { return JSON.parse(localStorage.getItem('currentUser') || 'null'); } catch { return null; }
}

/** Головна → усі ТПА (активні й неактивні) + «Перевірка» → drawer без фото */
function QCHomeTpaPanel({ onOpenCheck }) {
  const [machines, setMachines] = useState([]);
  const [lastByOrder, setLastByOrder] = useState({});
  const [openByOrder, setOpenByOrder] = useState({});
  const [showIdle, setShowIdle] = useState(true);
  const [loading, setLoading] = useState(true);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [floorRes, oqcRes] = await Promise.all([
        dashboardService.shiftFloor({ reports_limit: 10 }),
        orderQualityCheckService.getAll(),
      ]);
      const cards = floorRes.data?.machines || floorRes.data?.tpa_cards || [];
      setMachines(Array.isArray(cards) ? cards : []);

      const all = oqcRes.data?.results || oqcRes.data || [];
      const list = Array.isArray(all) ? all : [];
      const lastMap = {};
      const openMap = {};
      list.forEach((row) => {
        const oid = row.order;
        if (!oid) return;
        if (row.status === 'new' || row.status === 'repeat') {
          const prevOpen = openMap[oid];
          if (!prevOpen || Number(row.sequence || 0) > Number(prevOpen.sequence || 0)) {
            openMap[oid] = row;
          }
          return;
        }
        if (row.status !== 'done') return;
        const prev = lastMap[oid];
        if (!prev || String(row.checked_at || '') > String(prev.checked_at || '')) {
          lastMap[oid] = row;
        }
      });
      setLastByOrder(lastMap);
      setOpenByOrder(openMap);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Помилка завантаження');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, [load]);

  const rows = useMemo(() => {
    const rank = { stop: 0, pause: 1, setup: 2, running: 3, idle: 4 };
    return [...machines]
      .filter((m) => showIdle || m.status !== 'idle')
      .sort((a, b) => {
        const aActive = a.order ? 0 : 1;
        const bActive = b.order ? 0 : 1;
        if (aActive !== bActive) return aActive - bActive;
        const ra = rank[a.status] ?? 9;
        const rb = rank[b.status] ?? 9;
        if (ra !== rb) return ra - rb;
        return String(a.identifier || '').localeCompare(String(b.identifier || ''), 'uk', { numeric: true });
      });
  }, [machines, showIdle]);

  const stats = useMemo(() => {
    const total = machines.length;
    const withOrder = machines.filter((m) => m.order).length;
    const idle = machines.filter((m) => m.status === 'idle').length;
    return { total, withOrder, idle };
  }, [machines]);

  async function handleCheck(card) {
    if (!card?.order?.id) {
      alert('Немає активного замовлення на цьому ТПА');
      return;
    }
    setActionLoadingId(card.machine_id);
    try {
      const orderId = card.order.id;
      const pickProductionOpen = (list) => {
        const arr = Array.isArray(list) ? list : [];
        const open = arr.filter((i) => i.status === 'new' || i.status === 'repeat');
        return (
          open.find((i) => (i.check_location || 'production') === 'production')
          || open[0]
          || null
        );
      };
      const findOpen = async () => {
        const res = await orderQualityCheckService.getAll({ order: orderId, status: 'new' });
        const list = res.data?.results || res.data || [];
        const fromNew = pickProductionOpen(list);
        if (fromNew) return fromNew;
        const res2 = await orderQualityCheckService.getAll({ order: orderId, status: 'repeat' });
        const list2 = res2.data?.results || res2.data || [];
        return pickProductionOpen(list2);
      };

      let open = pickProductionOpen(
        [openByOrder[orderId], openByOrder[String(orderId)]].filter(Boolean),
      ) || await findOpen();

      // Складська відкрита заявка ≠ перевірка біля ТПА — створюємо production
      if (open && open.check_location === 'warehouse') {
        open = null;
      }

      if (!open) {
        const trig = await qualityCheckService.trigger({
          machines: [card.machine_id],
          order: orderId,
          source: 'vky_home',
        });
        const oqcIds = trig.data?.order_quality_check_ids || [];
        if (oqcIds.length) {
          const full = await orderQualityCheckService.getById(oqcIds[0]);
          onOpenCheck?.(full.data, { hidePhoto: true });
          await load();
          return;
        }
        open = await findOpen();
      }

      if (!open?.id) {
        // Останній шанс — пряме створення заявки на замовлення
        try {
          const created = await orderQualityCheckService.create({
            order: orderId,
            status: 'new',
            check_location: 'production',
          });
          if (created?.data?.id) {
            onOpenCheck?.(created.data, { hidePhoto: true });
            await load();
            return;
          }
        } catch (createErr) {
          const detail = createErr?.response?.data?.detail
            || createErr?.response?.data?.order?.[0]
            || createErr?.message;
          alert(detail || 'Не вдалося створити / знайти перевірку ВКЯ');
          return;
        }
        alert('Не вдалося створити / знайти перевірку ВКЯ для цього замовлення');
        return;
      }
      try {
        const full = await orderQualityCheckService.getById(open.id);
        onOpenCheck?.(full.data, { hidePhoto: true });
      } catch {
        onOpenCheck?.(open, { hidePhoto: true });
      }
      await load();
    } catch (e) {
      const d = e?.response?.data;
      const msg = (typeof d?.detail === 'string' && d.detail)
        || (Array.isArray(d?.detail) ? d.detail.join(' ') : null)
        || e?.message
        || 'Помилка відкриття ВКЯ';
      alert(msg);
    } finally {
      setActionLoadingId(null);
    }
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1.5} mb={1.5} flexWrap="wrap">
        <Typography variant="subtitle1" fontWeight={700}>ТПА</Typography>
        <Chip size="small" label={`Усього ${stats.total}`} />
        <Chip size="small" color="success" label={`З замовленням ${stats.withOrder}`} />
        <Chip size="small" label={`Вільні ${stats.idle}`} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={showIdle ? 'all' : 'active'}
          onChange={(_, v) => { if (v) setShowIdle(v === 'all'); }}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.25, fontSize: 12 } }}
        >
          <ToggleButton value="all">Усі ТПА</ToggleButton>
          <ToggleButton value="active">Лише з роботою</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Оновити">
          <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
        {loading && <CircularProgress size={18} />}
      </Box>
      {error && (
        <Typography color="error" variant="body2" mb={1}>{error}</Typography>
      )}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Список ТПА</TableCell>
              <TableCell>Остання перевірка</TableCell>
              <TableCell>Виготовлено</TableCell>
              <TableCell>Оператор</TableCell>
              <TableCell>Виріб / замовлення</TableCell>
              <TableCell>Час</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>
                    Немає ТПА для відображення
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((m) => {
              const order = m.order || null;
              const last = order ? lastByOrder[order.id] : null;
              const openQc = order ? openByOrder[order.id] : null;
              const fact = order
                ? (order.produced_fact
                  ?? order.progress?.fact_qty
                  ?? order.progress?.counter?.theory_qty
                  ?? 0)
                : null;
              const plan = order
                ? (order.quantity ?? order.progress?.plan_qty ?? null)
                : null;
              const factAtLast = last?.quantity_checked != null ? Number(last.quantity_checked) : null;
              const sinceLast = (fact != null && factAtLast != null)
                ? Math.max(0, Number(fact) - factAtLast)
                : null;
              const pct = (fact != null && plan > 0)
                ? Math.round((1000 * Number(fact)) / Number(plan)) / 10
                : null;
              const canCheck = Boolean(order?.id);

              return (
                <TableRow
                  key={m.machine_id}
                  hover
                  sx={{ opacity: order ? 1 : 0.72, bgcolor: openQc ? 'rgba(25, 118, 210, 0.04)' : undefined }}
                >
                  <TableCell sx={{ minWidth: 140 }}>
                    <Typography fontWeight={700} variant="body2">
                      {fmtMachineLabel(m) || m.identifier || `ТПА #${m.machine_id}`}
                    </Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                      <Chip
                        size="small"
                        label={m.status_label || m.status}
                        color={
                          m.status === 'running' ? 'success'
                            : m.status === 'stop' ? 'error'
                              : m.status === 'idle' ? 'default'
                                : 'warning'
                        }
                        sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                      />
                      {m.pause_reason_label && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={m.pause_reason_label}
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      )}
                      {openQc && (
                        <Chip
                          size="small"
                          color="info"
                          label={openQc.status === 'repeat' ? 'Очікує повтор' : 'Очікує перевірку'}
                          sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ minWidth: 150 }}>
                    {last ? (
                      <>
                        <Typography variant="body2">{fmtDate(last.checked_at)}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {QC_RESULT_LABELS[last.result] || last.result_display || '—'}
                          {last.inspector_name ? ` · ${last.inspector_name}` : ''}
                        </Typography>
                        {last.sequence != null && (
                          <Typography variant="caption" color="text.secondary">
                            ВКЯ #{last.sequence}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Ще не перевіряли</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    {order ? (
                      <>
                        <Typography variant="body2" fontWeight={700}>
                          {sinceLast != null
                            ? `+${sinceLast} з перевірки`
                            : (fact != null ? `${fact} від запуску` : '—')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {(fact != null && plan != null)
                            ? `План: ${fact} / ${plan}${pct != null ? ` (${pct}%)` : ''}`
                            : (fact != null ? `Факт: ${fact}` : '—')}
                        </Typography>
                      </>
                    ) : '—'}
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    {m.operator?.name || '—'}
                    {m.operator?.source === 'assigned' && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        на зміні
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>
                    {order ? (
                      <>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 240 }} fontWeight={600}>
                          {order.product_name || '—'}
                        </Typography>
                        {order.product_article && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {order.product_article}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" display="block">
                          {order.order_label
                            || (order.customer_order_number
                              ? `№${order.customer_order_number} · ВЗ #${order.id}`
                              : `ВЗ #${order.id}`)}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Немає замовлення</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ minWidth: 140 }}>
                    {order ? (
                      <>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Запуск: {fmtDate(order.launched_at || order.setup_at)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          План до: {fmtDate(order.planned_end)}
                        </Typography>
                      </>
                    ) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={canCheck ? 'contained' : 'outlined'}
                      disabled={!canCheck || actionLoadingId === m.machine_id}
                      onClick={() => handleCheck(m)}
                      sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
                    >
                      {actionLoadingId === m.machine_id
                        ? <CircularProgress size={14} color="inherit" />
                        : 'Перевірка'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

/** Підрозділи Головної, які ще не готові */
function QCHomeComingSoon({ title }) {
  return (
    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        Розділ у розробці
      </Typography>
    </Paper>
  );
}

const HISTORY_KIND = {
  check: { label: 'Перевірка', color: 'info' },
  result: { label: 'Результат', color: 'success' },
  blocking: { label: 'Блокування', color: 'error' },
  decision: { label: 'Рішення', color: 'warning' },
};

function buildHistoryEvents(checks, journal) {
  const events = [];

  (Array.isArray(checks) ? checks : []).forEach((row) => {
    // Створення / відкриття перевірки
    events.push({
      id: `oqc-open-${row.id}`,
      at: row.created_at,
      kind: 'check',
      title: row.status === 'repeat' ? `Повторна перевірка ВКЯ #${row.sequence}` : `Перевірка ВКЯ #${row.sequence}`,
      detail: row.check_location === 'warehouse'
        ? 'Місце: склад ГП'
        : (row.check_location_display || row.check_location || ''),
      product: row.order_product_name || '',
      article: row.order_product_article || '',
      machine: row.order_machine_identifier || '',
      orderLabel: row.order_customer_order_number
        ? `№${row.order_customer_order_number}`
        : (row.order ? `ВЗ #${row.order}` : ''),
      actor: '',
      qty: null,
      result: null,
      note: '',
      source: 'oqc',
      oqc: row,
    });

    if (row.status === 'done' && row.checked_at) {
      events.push({
        id: `oqc-done-${row.id}`,
        at: row.checked_at,
        kind: 'result',
        title: `Результат: ${QC_RESULT_LABELS[row.result] || row.result_display || '—'}`,
        detail: [
          row.defect_reason_label,
          row.stop_machine === true ? 'ТПА зупинено' : row.stop_machine === false ? 'ТПА працює далі' : '',
          row.retry_at ? `Повтор до ${fmtDate(row.retry_at)}` : '',
        ].filter(Boolean).join(' · '),
        product: row.order_product_name || '',
        article: row.order_product_article || '',
        machine: row.order_machine_identifier || '',
        orderLabel: row.order_customer_order_number
          ? `№${row.order_customer_order_number}`
          : (row.order ? `ВЗ #${row.order}` : ''),
        actor: row.inspector_name || '',
        qty: null,
        result: row.result || null,
        note: row.comment || '',
        source: 'oqc',
        oqc: row,
      });
    }
  });

  (Array.isArray(journal) ? journal : []).forEach((row) => {
    const shipApproved = !!(row.extra && row.extra.ship_approved);
    const isCreate = row.action === 'created';
    const kind = isCreate ? 'blocking' : 'decision';
    let title = row.action_display || row.action || 'Дія блокування';
    if (shipApproved) title = 'Погоджено відвантаження (тимч. допуск)';
    const qtyParts = [];
    if (row.quantity) qtyParts.push(`${row.quantity} шт`);
    if (row.quantity_good) qtyParts.push(`придатна ${row.quantity_good}`);
    if (row.quantity_scrap) qtyParts.push(`брак ${row.quantity_scrap}`);
    events.push({
      id: `bsj-${row.id}`,
      at: row.created_at,
      kind,
      title,
      detail: qtyParts.join(' · '),
      product: row.product_name || '',
      article: row.product_article || '',
      machine: row.machine_identifier || '',
      orderLabel: row.production_order_label || (row.production_order_id ? `ВЗ #${row.production_order_id}` : ''),
      actor: row.staff_name || '',
      qty: row.quantity || null,
      result: null,
      note: row.note || '',
      source: 'journal',
      journal: row,
      action: row.action,
    });
  });

  return events.sort((a, b) => String(b.at || '').localeCompare(String(a.at || '')));
}

function QCHistoryPanel({ onOpenCheck }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [resultFilter, setResultFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [oqcRes, journalRes] = await Promise.all([
        orderQualityCheckService.getAll(),
        blockedStockService.journal({ limit: 500 }),
      ]);
      const checks = oqcRes.data?.results || oqcRes.data || [];
      const journal = journalRes.data?.results || journalRes.data || [];
      setEvents(buildHistoryEvents(checks, journal));
    } catch (e) {
      console.error(e);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let rows = events;
    if (kindFilter !== 'all') {
      rows = rows.filter((r) => r.kind === kindFilter);
    }
    if (resultFilter !== 'all') {
      rows = rows.filter((r) => r.kind === 'result' && r.result === resultFilter);
    }
    if (dateFrom) {
      const from = new Date(`${dateFrom}T00:00:00`);
      rows = rows.filter((r) => r.at && new Date(r.at) >= from);
    }
    if (dateTo) {
      const to = new Date(`${dateTo}T23:59:59`);
      rows = rows.filter((r) => r.at && new Date(r.at) <= to);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) => (
        (r.title || '').toLowerCase().includes(q)
        || (r.product || '').toLowerCase().includes(q)
        || (r.article || '').toLowerCase().includes(q)
        || (r.machine || '').toLowerCase().includes(q)
        || (r.actor || '').toLowerCase().includes(q)
        || (r.orderLabel || '').toLowerCase().includes(q)
        || (r.note || '').toLowerCase().includes(q)
        || (r.detail || '').toLowerCase().includes(q)
      ));
    }
    return rows;
  }, [events, search, kindFilter, resultFilter, dateFrom, dateTo]);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={1.5}>
        Усі дії ВКЯ: перевірки, результати, блокування та рішення по продукції.
      </Typography>
      <Box display="flex" alignItems="center" gap={1.5} mb={1.5} flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Пошук: виріб, ТПА, контролер, замовлення…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 220, maxWidth: 360 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          size="small"
          type="date"
          label="З"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <TextField
          size="small"
          type="date"
          label="До"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 150 }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={kindFilter}
          onChange={(_, v) => { if (v) { setKindFilter(v); if (v !== 'result') setResultFilter('all'); } }}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.25, fontSize: 12 } }}
        >
          <ToggleButton value="all">Усі</ToggleButton>
          <ToggleButton value="check">Перевірки</ToggleButton>
          <ToggleButton value="result">Результати</ToggleButton>
          <ToggleButton value="blocking">Блокування</ToggleButton>
          <ToggleButton value="decision">Рішення</ToggleButton>
        </ToggleButtonGroup>
        {(kindFilter === 'all' || kindFilter === 'result') && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={resultFilter}
            onChange={(_, v) => { if (v) setResultFilter(v); }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: 1.25, fontSize: 12 } }}
          >
            <ToggleButton value="all">Результат: усі</ToggleButton>
            <ToggleButton value="passed">Пройшов</ToggleButton>
            <ToggleButton value="temp_allowance">Тимч. допуск</ToggleButton>
            <ToggleButton value="failed">Не пройшов</ToggleButton>
          </ToggleButtonGroup>
        )}
        <IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
        {loading && <CircularProgress size={18} />}
        {!loading && (
          <Typography variant="caption" color="text.secondary">
            Показано {filtered.length} з {events.length}
          </Typography>
        )}
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Дата</TableCell>
              <TableCell>Тип</TableCell>
              <TableCell>Подія</TableCell>
              <TableCell>ТПА</TableCell>
              <TableCell>Виріб / замовлення</TableCell>
              <TableCell>Хто</TableCell>
              <TableCell>Примітка</TableCell>
              <TableCell align="right">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={3}>Немає записів</Typography>
                </TableCell>
              </TableRow>
            )}
            {filtered.map((row) => {
              const meta = HISTORY_KIND[row.kind] || HISTORY_KIND.check;
              return (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(row.at)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={meta.label} color={meta.color} sx={{ fontWeight: 600 }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{row.title}</Typography>
                    {row.detail && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.detail}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.machine || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 220 }}>
                      {row.product || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {[row.article, row.orderLabel].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{row.actor || '—'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }} title={row.note || ''}>
                      {row.note || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {row.oqc ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => onOpenCheck?.(row.oqc)}
                        sx={{ textTransform: 'none' }}
                      >
                        Відкрити
                      </Button>
                    ) : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default function QCHubPage({ currentUser: currentUserProp }) {
  const currentUser = currentUserProp || readStoredUser();
  const perms = currentUser?.effective_permissions || {};
  const [searchParams, setSearchParams] = useSearchParams();

  const tabs = useMemo(
    () => MAIN_TABS.filter((t) => !t.perm || perms[t.perm] === true),
    [perms],
  );

  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab');
    if (tabs.some((t) => t.id === tab)) return tab;
    return 'home';
  }, [searchParams, tabs]);

  const homeSub = searchParams.get('scope') || 'tpa';

  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'home') {
      next.delete('tab');
    } else {
      next.set('tab', tab);
    }
    if (tab !== 'home') next.delete('scope');
    // keep highlight/order only for requests
    if (tab !== 'requests') {
      next.delete('highlight');
      next.delete('order');
    }
    setSearchParams(next, { replace: true });
  };

  const setHomeSub = (scope) => {
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    if (scope === 'tpa') next.delete('scope');
    else next.set('scope', scope);
    setSearchParams(next, { replace: true });
  };

  const [drawerItem, setDrawerItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerHidePhoto, setDrawerHidePhoto] = useState(false);
  const [homeKey, setHomeKey] = useState(0);

  const openCheck = useCallback((item, opts = {}) => {
    setDrawerItem(item);
    setDrawerHidePhoto(Boolean(opts.hidePhoto));
    setDrawerOpen(true);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)', overflow: 'hidden' }}>
      <Box sx={{ flexShrink: 0, mb: 1.5, display: 'grid', gridTemplateColumns: `repeat(${tabs.length}, 1fr)`, gap: 1 }}>
        {tabs.map((t) => (
          <Button
            key={t.id}
            variant={activeTab === t.id ? 'contained' : 'outlined'}
            startIcon={t.icon}
            onClick={() => setActiveTab(t.id)}
            sx={{ py: 1.25, fontSize: '0.875rem', textTransform: 'none', fontWeight: 700 }}
            fullWidth
          >
            {t.label}
          </Button>
        ))}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
        {activeTab === 'home' && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              {HOME_SUB.map((s) => (
                <Button
                  key={s.id}
                  size="small"
                  variant={homeSub === s.id ? 'contained' : 'outlined'}
                  startIcon={s.icon}
                  onClick={() => setHomeSub(s.id)}
                  sx={{ textTransform: 'none', fontWeight: 700 }}
                >
                  {s.label}
                </Button>
              ))}
            </Box>
            {homeSub === 'tpa' && (
              <QCHomeTpaPanel key={homeKey} onOpenCheck={openCheck} />
            )}
            {homeSub === 'production' && (
              <QCHomeComingSoon title="Виробництво" />
            )}
            {homeSub === 'warehouse' && (
              <QCHomeComingSoon title="Склад" />
            )}
          </Box>
        )}

        {activeTab === 'requests' && (
          <QCKanbanPage embedded />
        )}

        {activeTab === 'blocking' && (
          <BlockingStockPage currentUser={currentUser} />
        )}

        {activeTab === 'history' && (
          <QCHistoryPanel onOpenCheck={openCheck} />
        )}
      </Box>

      <QCResultDrawer
        open={drawerOpen}
        item={drawerItem}
        hidePhoto={drawerHidePhoto}
        onClose={() => {
          setDrawerOpen(false);
          setDrawerHidePhoto(false);
        }}
        onDone={() => {
          setHomeKey((k) => k + 1);
        }}
        currentUser={currentUser}
      />
    </Box>
  );
}
